import React, { useState, useEffect } from 'react';

const FilePreview = ({ document, isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);

  useEffect(() => {
    if (isOpen && document) {
      loadPreview();
    }
  }, [isOpen, document]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/documents/${document.id}/preview`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to load preview');
      }
      
      // Handle different content types
      if (document.mimeType.startsWith('text/') || 
          document.mimeType === 'application/json' || 
          document.mimeType === 'application/xml') {
        const text = await response.text();
        setPreviewContent({ type: 'text', content: text });
      } else if (document.mimeType.startsWith('image/')) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewContent({ type: 'image', url });
      } else if (document.mimeType === 'application/pdf') {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPreviewContent({ type: 'pdf', url });
      } else {
        // Unsupported file type
        setPreviewContent({ type: 'unsupported' });
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.startsWith('text/')) return '📝';
    if (mimeType === 'application/json') return '📊';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📊';
    if (mimeType.includes('document') || mimeType.includes('word')) return '📝';
    return '📎';
  };

  const handleDownload = () => {
    window.open(`/api/documents/${document.id}/download`, '_blank');
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-400 border-t-transparent"></div>
            <p className="text-gray-500 mt-2">Loading preview...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center text-red-600">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p>Failed to load preview</p>
            <p className="text-sm text-gray-500 mt-1">{error}</p>
          </div>
        </div>
      );
    }

    if (!previewContent) return null;

    switch (previewContent.type) {
      case 'text':
        return (
          <div className="h-96 overflow-auto bg-gray-50 p-4 rounded-lg">
            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
              {previewContent.content}
            </pre>
          </div>
        );

      case 'image':
        return (
          <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
            <img 
              src={previewContent.url}
              alt={document.fileName}
              className="max-w-full max-h-full object-contain rounded"
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="h-96 bg-gray-50 rounded-lg">
            <iframe
              src={previewContent.url}
              width="100%"
              height="100%"
              className="rounded-lg"
              title={document.fileName}
            />
          </div>
        );

      case 'unsupported':
        return (
          <div className="flex items-center justify-center h-96">
            <div className="text-center text-gray-500">
              <span className="text-4xl mb-4 block">{getFileIcon(document.mimeType)}</span>
              <p className="text-lg font-medium">{document.fileName}</p>
              <p className="text-sm mb-4">Preview not available for this file type</p>
              <button
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download File
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{getFileIcon(document.mimeType)}</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{document.fileName}</h3>
              <p className="text-sm text-gray-500">
                {document.mimeType} • {(document.fileSize / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
              title="Download"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
              title="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-hidden">
          {renderPreviewContent()}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
          {document.description && (
            <p className="mb-2">{document.description}</p>
          )}
          <p>
            Uploaded on {new Date(document.uploadedAt).toLocaleDateString()} by {document.uploadedBy}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FilePreview;